<template>
    <ModalIndex
        v-model:visible="visible"
        bodyClass="overflow-y-auto flex-1"
        footerClass="hidden"
        fullscreenOnMobile
        title="Share payment link"
        wrapperClass="flex flex-col max-h-[95vh]"
    >
        <div class="flex flex-col gap-6">
            <p class="text-content-md text-content-secondary">
                Share this link with clients and let them pay their early deposit seamlessly.
            </p>

            <div class="rounded-lg border border-ds-primary-200 bg-ds-primary-50 p-3">
                <span class="text-content-md text-content-secondary">Wire instructions</span>
                <p class="font-medium text-content-primary">{{ wireDocName }}</p>
            </div>

            <DsTextInput
                v-model="openLinkUrl"
                label="Payment link URL"
                readonly
                type="text"
            >
                <template #trailing>
                    <DsIconButton
                        :hideTooltipOnClick="false"
                        icon="fa-regular fa-clone"
                        size="sm"
                        :tooltipText="urlCopied ? 'Copied!' : 'Copy'"
                        @click="copyUrl"
                    />
                </template>
            </DsTextInput>

            <div class="border-t pt-4">
                <button
                    :aria-expanded="isGeneratorExpanded"
                    class="flex w-full items-center justify-between rounded-lg border border-ds-primary-200 p-3 text-left transition-colors hover:bg-ds-primary-50"
                    @click="isGeneratorExpanded = !isGeneratorExpanded"
                >
                    <p class="text-content-md text-content-secondary">Generate a custom button to embed on your website.</p>
                    <DsIcon
                        class="ml-3 shrink-0 text-content-secondary"
                        :icon="isGeneratorExpanded ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                        size="sm"
                    />
                </button>

                <div
                    v-if="isGeneratorExpanded"
                    class="mt-4 flex flex-col gap-4"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <DsTextInput
                            v-model="buttonText"
                            label="Button text"
                            :maxlength="40"
                            placeholder="Make a payment"
                            type="text"
                        />

                        <div class="flex flex-col gap-1">
                            <span class="text-content-md font-medium text-content-secondary">Button color</span>
                            <div class="flex gap-2">
                                <input
                                    v-model="buttonColor"
                                    aria-label="Button background color"
                                    class="h-10 w-12 cursor-pointer rounded border p-1"
                                    type="color"
                                />
                                <DsTextInput
                                    v-model="buttonColor"
                                    :maxlength="9"
                                    placeholder="#"
                                    type="text"
                                    @focusout="onHexBlur"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col gap-1">
                        <span class="text-content-md font-medium text-content-secondary">Preview</span>
                        <div class="flex justify-center rounded border border-ds-primary-200 p-4">
                            <a
                                class="preview-anchor inline-flex items-center rounded px-6 py-2.5 text-sm font-semibold no-underline"
                                :href="openLinkUrl"
                                rel="noopener noreferrer"
                                :style="{ backgroundColor: validatedColor, color: textColor }"
                                target="_blank"
                            >
                                <svg
                                    class="mr-1.5 inline-block shrink-0 align-middle"
                                    :fill="textColor"
                                    height="16"
                                    viewBox="0 0 28 31"
                                    width="14"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path :d="LOGO_SVG_PATH" />
                                </svg>
                                {{ buttonText || DEFAULT_BUTTON_TEXT }}
                            </a>
                        </div>
                    </div>

                    <div class="flex flex-col gap-2">
                        <DsTextarea
                            class="font-mono text-xs"
                            label="HTML code"
                            :modelValue="htmlSnippet"
                            readonly
                            :rows="3"
                        />
                        <DsButton
                            class="self-start"
                            size="md"
                            variant="secondary"
                            @click="copyHtml"
                        >
                            <template #leading>
                                <DsIcon
                                    icon="fa-regular fa-clone"
                                    size="sm"
                                />
                            </template>
                            {{ htmlCopied ? 'Copied!' : 'Copy HTML' }}
                        </DsButton>
                    </div>
                </div>
            </div>
        </div>
    </ModalIndex>
</template>

<script setup lang="ts">
import ModalIndex from '@/components/Components/Modal/ModalIndex.vue';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsIconButton from '@/components/design-system/button/DsIconButton.vue';
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsTextInput from '@/components/design-system/form/input/text/DsTextInput.vue';
import DsTextarea from '@/components/design-system/form/input/text/DsTextarea.vue';
import {
    buildAnchorHtml,
    DEFAULT_BUTTON_TEXT,
    expandHex,
    getContrastTextColor,
    isValidHex,
    LOGO_SVG_PATH,
    resolveButtonColor,
} from '@/components/Views/Company/Settings/open-link-button.helpers';
import { useClipboard, useEventListener } from '@vueuse/core';
import { computed, ref } from 'vue';

const visible = ref(false);
const wireDocName = ref('');
const openLinkUrl = ref('');
const buttonText = ref(DEFAULT_BUTTON_TEXT);
// Shared by the color picker and the hex text input; may briefly hold a partial hex while typing.
const buttonColor = ref('#000000');
const isGeneratorExpanded = ref(false);

const validatedColor = computed(() => resolveButtonColor(buttonColor.value));
const textColor = computed(() => getContrastTextColor(validatedColor.value));

const htmlSnippet = computed(() =>
    buildAnchorHtml({
        anchorText: buttonText.value,
        url: openLinkUrl.value,
        backgroundColor: validatedColor.value,
        textColor: textColor.value,
    }),
);

const onHexBlur = () => {
    const withHash = buttonColor.value.startsWith('#') ? buttonColor.value : `#${buttonColor.value}`;
    const expanded = expandHex(withHash);
    if (isValidHex(expanded)) {
        buttonColor.value = expanded;
    }
};

const { copy: writeUrlToClipboard, copied: urlCopied } = useClipboard({ source: openLinkUrl });
const { copy: writeHtmlToClipboard, copied: htmlCopied } = useClipboard({ source: htmlSnippet });

// useClipboard's copy() takes an optional override value, so it can't be a template event
// handler directly: it would receive the MouseEvent and copy that instead of the source.
const copyUrl = () => writeUrlToClipboard();
const copyHtml = () => writeHtmlToClipboard();

const onIconClick = (e: MouseEvent) => {
    const target = (e.target as HTMLElement).closest('.OpenLinkSettingsModal');
    if (!(target instanceof HTMLAnchorElement)) return;

    openLinkUrl.value = target.dataset.openLinkUrl ?? '';
    wireDocName.value = target.dataset.wiredocName ?? '';
    buttonText.value = DEFAULT_BUTTON_TEXT;
    buttonColor.value = '#000000';
    isGeneratorExpanded.value = false;
    visible.value = true;
};

useEventListener(document, 'click', onIconClick);
</script>

<style scoped>
.preview-anchor {
    font-family: Arial, sans-serif;
}
</style>
